<template>
  <div v-if="!enterAddressManually">
    <FormField :overrideClass="'search-input-wrapper' + (isNotGrid ? ' not-grid' : '')">
      <FormInput
        :id="searchInputId"
        @input="searchDebounce(addressSearch)"
        v-model="addressSearchKey"
        :placeholder="isPOBox ? 'e.g. PO Box 7518, Melbourne' : 'e.g. 380 St Kilda Rd, Melbourne'"
        :disabled="disabled"
        autocomplete="street-address"
      />
      <div v-if="searching" class="spinner-container"><Spinner /></div>

      <!-- Address search results -->
      <ul v-if="!disabled" class="address-suggestion" v-click-outside="clearList">
        <li v-for="address in addressSearchResults" :key="address.$.RecordId">
          <button class="non-button-style" @click.prevent="selectAddress(address.$.RecordId)">
            {{ address.$.AddressLine }} {{ address.$.Locality }} {{ address.$.Postcode }}
            {{ address.$.State }}
          </button>
        </li>
      </ul>

      <!-- If search input invalid -->
      <template v-if="v$.addressSearchKey.$anyDirty">
        <!-- Error message: empty residential address search key -->
        <label v-if="v$.addressSearchKey.$invalid && residentialState !== ''" :for="searchInputId" class="error">
          {{ ERROR_MESSAGES.SEARCH_INPUT_ERROR.EMPTY_RESIDENTIAL }}
        </label>

        <!-- Error message: invalid po box search key -->
        <label v-else-if="residentialState === '' && isPOBox && v$.addressSearchKey.poBoxFormat.$invalid" :for="searchInputId" class="error">
          {{ ERROR_MESSAGES.INVALID_POBOX }}
        </label>

        <!-- Error message: address not found -->
        <label v-else-if="searchCanShowErrorMsg && !searching && v$.addressSearchKey.$invalid && addressSearchResults.length === 0" :for="searchInputId" class="error">
          {{ ERROR_MESSAGES.SEARCH_INPUT_ERROR.ADDRESS_NOT_FOUND }}
        </label>
      </template>
    </FormField>

    <a v-if="!disabled" @click.prevent="switchMode" role="button" href="#">Fill out your address manually</a>
  </div>

  <div v-else>
    <div :key="isPOBox">
      <!-- Only show if isPOBox prop is true -->
      <FormField
        v-if="isPOBox"
        :overrideClass="isNotGrid ? 'not-grid' : ''"
        labelText="PO Box"
        placeholder="e.g. PO Box 7518"
        @input="emitAddressDetails('addressLine1')"
        v-model="addressObj.addressLine1"
        :error="v$.addressObj.addressLine1.$anyDirty && v$.addressObj.addressLine1.$invalid"
        :errorMessage="addressObj.addressLine1 === '' ? ERROR_MESSAGES.FORM_FIELDS.POBOX : ERROR_MESSAGES.INVALID_POBOX"
      />

      <!-- If isPOBox prop is false, show this instead -->
      <template v-else>
        <FormField
          :overrideClass="isNotGrid ? 'not-grid address-line-margin-bottom' : 'address-line-margin-bottom'"
          placeholder="e.g. 380 St Kilda Road"
          labelText="Street address"
          @input="emitAddressDetails('addressLine1')"
          v-model="addressObj.addressLine1"
          autocomplete="street-address"
          :error="v$.addressObj.addressLine1.$anyDirty && v$.addressObj.addressLine1.$invalid"
          :errorMessage="
            addressObj.addressLine1 === ''
              ? ERROR_MESSAGES.FORM_FIELDS.ADDRESS_LINE
              : stringGreaterThanMaxCharacters(addressObj.addressLine1, 60)
              ? ERROR_MESSAGES.INVALID_SYMBOLS
              : ERROR_MESSAGES.MAX_CHARACTERS_EXCEEDED
          "
        />
        <FormField
          :overrideClass="isNotGrid ? 'not-grid address-line-margin-bottom' : 'address-line-margin-bottom'"
          placeholder="Address line 2 (optional)"
          :required="false"
          @input="emitAddressDetails('addressLine2')"
          v-model="addressObj.addressLine2"
          :error="v$.addressObj.addressLine2.$anyDirty && v$.addressObj.addressLine2.$invalid"
          :errorMessage="stringGreaterThanMaxCharacters(addressObj.addressLine2, 60) ? ERROR_MESSAGES.INVALID_SYMBOLS : ERROR_MESSAGES.MAX_CHARACTERS_EXCEEDED"
        />
        <FormField
          :overrideClass="isNotGrid ? 'not-grid address-line-margin-bottom' : 'address-line-margin-bottom'"
          placeholder="Address line 3 (optional)"
          :required="false"
          @input="emitAddressDetails('addressLine3')"
          v-model="addressObj.addressLine3"
          :error="v$.addressObj.addressLine3.$anyDirty && v$.addressObj.addressLine3.$invalid"
          :errorMessage="stringGreaterThanMaxCharacters(addressObj.addressLine3, 60) ? ERROR_MESSAGES.INVALID_SYMBOLS : ERROR_MESSAGES.MAX_CHARACTERS_EXCEEDED"
        />
      </template>

      <FormField
        :overrideClass="isNotGrid ? 'not-grid' : ''"
        labelText="Suburb"
        placeholder="e.g. Melbourne"
        @input="emitAddressDetails('suburb')"
        v-model="addressObj.suburb"
        autocomplete="address-level2"
        :error="v$.addressObj.suburb.$anyDirty && v$.addressObj.suburb.$invalid"
        :errorMessage="addressObj.suburb === '' ? ERROR_MESSAGES.FORM_FIELDS.SUBURB : ERROR_MESSAGES.INVALID_SYMBOLS"
      />
      <div class="grid-fieldset">
        <FormField
          :overrideClass="isNotGrid ? 'not-grid' : ''"
          labelText="Postcode"
          placeholder="e.g. 3000"
          autocomplete="postal-code"
          @input="emitAddressDetails('postcode')"
          v-model="addressObj.postcode"
          type="number"
          @keydown="numberInputOnly"
          :error="v$.addressObj.postcode.$anyDirty && v$.addressObj.postcode.$invalid"
          :errorMessage="ERROR_MESSAGES.FORM_FIELDS.POSTCODE"
        />
      </div>
      <FormField :overrideClass="isNotGrid ? 'not-grid' : ''" labelText="State">
        <DropdownSelect
          initialSelect="Please select a state or territory"
          :items="getDropdownOptions(stateOptions)"
          :disabled="stateLocked"
          v-model="addressObj.state"
          @input="emitAddressDetails('state')"
        />
        <label v-if="v$.addressObj.state.$anyDirty && v$.addressObj.state.$invalid" class="error">
          {{ ERROR_MESSAGES.FORM_FIELDS.STATE }}
        </label>
      </FormField>
    </div>
    <button class="non-button-style" @click="switchMode">Back to search</button>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import { AUSTRALIAN_STATES } from '@/constants';
import { poBoxFormat, numberInputOnly, stringWithInvalidSymbols, stringWithSymbolsNotSpace, stringGreaterThanMaxCharacters, validPostCode } from '@general/js/utils';
import { CLICK_OUTSIDE } from '@general/js/_customDirectives';
import Spinner from '@general/js/components/Spinner.vue';
import { ErrorStrings } from '@general/js/constants';
import JoinNowServices from '@general/js/services/JoinNow';
import FormField from '@/components/FormField.vue';
import FormInput from '@/components/FormInput.vue';
import DropdownSelect from './DropdownSelect.vue';

const apiTimeoutTime = 500;
let triggerAddressSearch;

const ERROR_MESSAGES = {
  INVALID_POBOX: 'Address must include PO Box e.g. PO Box 7518.',
  INVALID_SYMBOLS: ErrorStrings.INVALID_SYMBOLS,
  MAX_CHARACTERS_EXCEEDED: 'This field exceeds the 60 character limit. Please continue in the next address line.',
  SEARCH_INPUT_ERROR: {
    ADDRESS_NOT_FOUND: 'We can’t find that address, please try again or complete the fields below.',
    EMPTY_RESIDENTIAL: 'Please search for a residential address.',
  },
  FORM_FIELDS: {
    POBOX: 'Please provide a response for PO Box.',
    ADDRESS_LINE: 'Please provide a response for a street address.',
    SUBURB: ErrorStrings.SUBURB,
    POSTCODE: ErrorStrings.POSTCODE,
    STATE: 'Please provide a response for a state.',
  },
};

export default {
  name: 'addressForm',
  setup() { return { v$: useVuelidate() }; },

  directives: {
    'click-outside': CLICK_OUTSIDE,
  },

  components: {
    FormField,
    FormInput,
    Spinner,
    DropdownSelect,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * If given, id of address search input would be given string
     */
    id: {
      type: String,
    },

    /**
     * If true, search key will be prefiled based on the address object given in preFilledDetails prop
     * default to be true
     */
    prefillSearchKey: {
      type: Boolean,
      default: true,
    },

    /**
     * If given, address form will consider it is used for residential address. Otherwise, will consider postal address
     * If used, should always pass one of Australian state return value.
     * By default is empty string.
     */
    residentialState: {
      type: String,
      default: '',
      validator(value) {
        const enumArr = AUSTRALIAN_STATES.map((obj) => {
          return obj.returnValue;
        });

        enumArr.push('');
        // must be one of these strings
        return enumArr.indexOf(value) !== -1;
      },
    },

    // If true, state can not be edited and `residentialState` prop must be given.
    stateLocked: {
      type: Boolean,
      default: false,
    },

    /**
     * This object must be exactly same structure as `addressObj`.
     * If given, address search bar and manual input form will be prefilled.
     */
    preFilledDetails: {
      type: Object,
    },

    isPOBox: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, address form will not be in grid layout
     */
    isNotGrid: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      searchCanShowErrorMsg: false,
      ERROR_MESSAGES,
      stateOptions: AUSTRALIAN_STATES,
      searching: false,
      enterAddressManually: false,
      addressSelected: false,
      addressSearchKey: '',
      addressSearchResults: [],
      addressObj: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        suburb: '',
        postcode: '',
        state: this.residentialState,
        dpId: '',
      },
      thisUuid: uuid(),
      isDirty: false,
    };
  },

  beforeMount() {
    if (this.preFilledDetails) {
      this.addressObj = {
        ...this.addressObj,
        ...this.preFilledDetails,
      };

      if (this.prefillSearchKey) {
        this.addressSearchKey = this.getFullAddress();
      }
    }
  },

  mounted() {
    this.emitAddressDetails();
    this.v$.$reset();
    this.isDirty = false;
  },

  watch: {
    addressSearchKey() {
      this.searchCanShowErrorMsg = false;
      this.clearList();
    },

    preFilledDetails(obj) {
      this.addressSearchKey = this.getFullAddress();
      this.addressObj = { ...this.addressObj, ...obj };

      // if is residential address search for JOL (used in addressValidation.vue)
      // if this condition meets, search key will be empty
      if (!this.isPOBox && this.stateLocked && !this.preFillSearchKey) {
        const allAddressObjKeys = Object.keys(this.addressObj);

        /**
         * since state is locked, and `preFillSearchKey` is false, we have to
         * check if other address fields are empty or not. reason for this is
         * in JOL, where state is already given for residential address, due to
         * how component is built, `preFilledDetails` will be updated which means
         * `addressSearchKey` won't be empty string on initial load when it should be.
         *
         * As so, this is the process to revert `addressSearchKey` to be empty only
         * when it is need to be.
         */
        const searchKeyShouldBeEmpty = allAddressObjKeys
          .filter((key) => {
            return key !== 'state' && key !== 'isPOBox';
          })
          .every((key) => {
            return this.addressObj[key] === '';
          });

        if (searchKeyShouldBeEmpty) {
          this.addressSearchKey = '';
        }
      }
    },

    'v$.$anyDirty': {
      handler(newVal, oldVal) {
        if (oldVal === false) {
          this.isDirty = true;
        }
      },
    },

    'v$.$invalid': {
      handler() {
        this.emitAddressDetails();
      },
    },

    isPOBox(newVal) {
      if (newVal === true || newVal === false) {
        // Clear all address fields when the checkbox value changes
        this.addressObj = {
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          suburb: '',
          postcode: '',
          state: this.residentialState, // Reset state to the default residentialState
          dpId: '',
        };
        this.emitAddressDetails();
      }
    },
  },

  validations() {
    return this.validationObj;
  },

  computed: {
    searchInputId() {
      return this.id ?? `${this.thisUuid}-search-input`;
    },

    validationObj() {
      const obj = {
        addressObj: {
          suburb: { required, stringWithInvalidSymbols },
          postcode: {
            required,
            validPostCode,
          },
          state: {
            required,
          },
        },
      };

      if (this.isPOBox) {
        obj.addressObj.addressLine1 = {
          required,
          stringWithSymbolsNotSpace,
          stringGreaterThanMaxCharacters: (str) => {
            return stringGreaterThanMaxCharacters(str, 60);
          },
          poBoxFormat(data) {
            return poBoxFormat(data);
          },
        };
      } else {
        obj.addressObj.addressLine1 = {
          required,
          stringWithInvalidSymbols,
          stringGreaterThanMaxCharacters: (str) => {
            return stringGreaterThanMaxCharacters(str, 60);
          },
        };
      }

      obj.addressObj.addressLine2 = {
        stringWithInvalidSymbols,
        stringGreaterThanMaxCharacters: (str) => {
          return stringGreaterThanMaxCharacters(str, 60);
        },
      };
      obj.addressObj.addressLine3 = {
        stringWithInvalidSymbols,
        stringGreaterThanMaxCharacters: (str) => {
          return stringGreaterThanMaxCharacters(str, 60);
        },
      };

      if (!this.enterAddressManually) {
        if (this.isPOBox) {
          obj.addressSearchKey = {
            required,
            poBoxFormat(data) {
              return poBoxFormat(data);
            },
          };
        } else {
          obj.addressSearchKey = {
            required: requiredIf(function (address) {
              return this.isDirty && Boolean(address?.addressSearchKey);
            }),
          };
        }
      }

      return obj;
    },
  },

  methods: {
    getFullAddress() {
      if (this.preFilledDetails) {
        const addressObject = this.preFilledDetails;
        const { addressLine1, addressLine2, addressLine3, suburb, postcode, state } = addressObject;

        if (this.isPOBox) {
          const poBox = this.preFilledDetails.addressLine1;
          return [poBox.toLowerCase().replace(/(po\sb)/, 'PO B'), suburb?.toUpperCase(), postcode, state?.toUpperCase()].filter(Boolean).join(' ');
        }

        return [addressLine1, addressLine2, addressLine3, suburb?.toUpperCase(), postcode, state?.toUpperCase()].filter(Boolean).join(' ');
      }
      return '';
    },

    emitAddressDetails(key) {
      this.v$.addressObj[key]?.$touch();

      const res = this.addressObj;
      const obj = {
        address: {
          suburb: res.suburb,
          postcode: res.postcode,
          state: res.state,
          dpId: res?.dpId || '',
          addressLine1: res.addressLine1,
          addressLine2: res.addressLine2,
          addressLine3: res.addressLine3,
        },
        invalid: this.v$.$invalid,
      };
      this.$emit('updateAddressDetail', obj);
    },

    searchDebounce(fn) {
      clearTimeout(triggerAddressSearch);
      triggerAddressSearch = setTimeout(async () => {
        await fn();
      }, apiTimeoutTime);
    },

    getDropdownOptions(options) {
      const dropdownOptions = options.map(({ display, returnValue }) => {
        return {
          name: display,
          value: returnValue,
        };
      });

      return dropdownOptions;
    },

    async addressSearch() {
      this.addressSelected = false;
      this.searching = true;

      try {
        const PredictiveAddressData = await JoinNowServices.getPredictiveAddress(this.addressSearchKey);
        let results;

        if (PredictiveAddressData?.DtResponse?.Result) {
          results = PredictiveAddressData.DtResponse.Result;

          if (this.residentialState !== '') {
            // If is residential address search, restrict the address suggestions to non PO Box addresses and the given residential state if required.
            results = results.filter((address) => {
              const notPOBox = !address.$.AddressLine.toLowerCase().startsWith('po box');
              if (this.stateLocked) {
                return address.$.State === this.residentialState && notPOBox;
              }
              return notPOBox;
            });
          } else {
            // If it's PO box address, filter the results of normal address out, vice versa
            results = results.filter((address) => {
              const condition = address.$.AddressLine.toLowerCase().startsWith('po box');
              return this.isPOBox ? condition : !condition;
            });
          }

          // Restrict the number of results to be 10 if there's more than 10 result
          if (results.length > 10) {
            results.splice(10);
          }

          this.addressSearchResults = results;
        }
      } catch (err) {
        logger.log(err);
      } finally {
        this.searching = false;
        this.searchCanShowErrorMsg = true;
      }
    },

    async selectAddress(recordId) {
      try {
        const result = await JoinNowServices.getAddressDetails(recordId);
        const addressDetail = result.DtResponse.Result[0].$;

        let addressLine1 = '';
        let addressLine2 = '';
        let addressLine3 = '';

        if (this.isPOBox) {
          // If PO Box, address line 1 will be PO Box address
          addressLine1 = addressDetail.AddressLine;
        } else {
          // For non PO box address
          // If exists, format lot number as "Lot [number] " i.e. Lot 5
          const lotNumber = addressDetail.LotNumber ? `Lot ${addressDetail.LotNumber} ` : '';

          // If exists, format level type and number as "[level] [number] " i.e. Level 1
          let levelTypeAndNumber = '';
          if (addressDetail.LevelType !== '' && addressDetail.LevelNumber !== '') {
            levelTypeAndNumber = `${addressDetail.LevelType} ${addressDetail.LevelNumber} `;
          } else if (addressDetail.LevelType !== '') {
            levelTypeAndNumber = `${addressDetail.LevelType} `;
          } else if (addressDetail.LevelNumber !== '') {
            levelTypeAndNumber = `${addressDetail.LevelNumber} `;
          }

          // If exists, format unit type and number as "[type] [number]/" i.e. Unit 2/
          let unitTypeAndNumber = '';
          if (addressDetail.UnitType !== '' && addressDetail.UnitNumber !== '') {
            unitTypeAndNumber = `${addressDetail.UnitType} ${addressDetail.UnitNumber}/`;
          } else if (addressDetail.UnitType !== '') {
            unitTypeAndNumber = `${addressDetail.UnitType} `;
          } else if (addressDetail.UnitNumber !== '') {
            unitTypeAndNumber = `${addressDetail.UnitNumber}/`;
          }

          // Combine as prefix as "Lot [lotNumber] [level] [levelNumber] [type] [unitNumber]/" i.e. Lot 5 Level 1 Unit 2/
          const prefix = levelTypeAndNumber !== '' || unitTypeAndNumber !== '' || lotNumber !== '' ? `${lotNumber}${levelTypeAndNumber}${unitTypeAndNumber}` : '';

          // Format street number as "[number1][suffix1] - [number2][suffix2] " if number2 or suffix2 exists i.e. 12A - 14B
          // Otherwise, format as "[number1][suffix1] " if number1 or suffix i.e. 12A
          let streetNumber = '';
          if (addressDetail.StreetNumber2 !== '' || addressDetail.StreetNumberSuffix2 !== '') {
            streetNumber = `${addressDetail.StreetNumber1}${addressDetail.StreetNumberSuffix1 || ''} - ${addressDetail.StreetNumber2}${addressDetail.StreetNumberSuffix2 || ''} `;
          } else if (addressDetail.StreetNumber1 !== '' || addressDetail.StreetNumberSuffix1 !== '') {
            streetNumber = `${addressDetail.StreetNumber1}${addressDetail.StreetNumberSuffix1 || ''} `;
          }

          // Format street name as "[name] [type]" i.e. Exhibition St
          const streetName = `${addressDetail.StreetName} ${addressDetail.StreetType}`.trim();

          // Combine as "[prefix][streetNumber][streetName]" i.e. Lot 5 Level 1 Unit 2/12A - 14B Exhibition St
          const formattedStreetAddress = `${prefix}${streetNumber}${streetName}`.trim();

          // If building name exists, it will be in address line 1,
          // otherwise, street address will be in address line 1
          if (addressDetail.BuildingName !== '') {
            addressLine1 = addressDetail.BuildingName;
            addressLine2 = formattedStreetAddress;
          } else {
            addressLine1 = formattedStreetAddress;
          }
        }

        // Apply overflow logic (address lines cannot be over 60 characters)
        ({ addressLine1, addressLine2, addressLine3 } = this.applyOverflowFormatting(addressLine1, addressLine2));

        // Update address object
        this.addressObj = {
          ...this.addressObj,
          addressLine1,
          addressLine2,
          addressLine3,
          suburb: addressDetail.Locality,
          postcode: addressDetail.Postcode,
          state: addressDetail.State,
          dpId: addressDetail.DPID,
        };

        this.addressSelected = true;
        this.switchMode();
        this.emitAddressDetails();
      } catch (err) {
        logger.log(err);
      }
    },

    applyOverflowFormatting(addressLine1, addressLine2) {
      let addressLine3 = '';
      let updatedAddressLine1 = addressLine1;
      let updatedAddressLine2 = addressLine2;

      if (updatedAddressLine1.length > 60) {
        updatedAddressLine2 = `${updatedAddressLine1.slice(60)} ${updatedAddressLine2}`.trim();
        updatedAddressLine1 = updatedAddressLine1.slice(0, 60);
      }

      if (updatedAddressLine2.length > 60) {
        addressLine3 = updatedAddressLine2.slice(60).trim();
        updatedAddressLine2 = updatedAddressLine2.slice(0, 60);
      }

      return { addressLine1: updatedAddressLine1, addressLine2: updatedAddressLine2, addressLine3 };
    },

    clearList() {
      this.addressSearchResults = [];
    },

    switchMode() {
      this.enterAddressManually = !this.enterAddressManually;
      this.searching = false;
      this.$emit('updateManualAddressEntry', this.enterAddressManually);
      this.clearList();
    },

    uuid,
    numberInputOnly,
    stringGreaterThanMaxCharacters,
  },
};
</script>

<style lang="scss" scoped>
.not-grid {
  display: block;
}

.search-input-wrapper {
  margin-bottom: spacer(2);

  .address-suggestion {
    @include suggestion-list-style;
  }

  .spinner-container {
    position: absolute;
    right: -20px;
    top: 0;
    transform: translateY(-25%) scale(0.5);
  }
}

.form-section {
  margin-bottom: rem(25);

  .grid-fieldset {
    @include grid-layout(spacer(1.5), spacer(0));
    @include grid-template(1fr 1fr);
  }

  .form-field {
    &.address-line-margin-bottom {
      margin-bottom: rem(7);
    }
  }
}

:deep(.non-button-style) {
  color: $CRIMSON;
}
</style>
